/* eslint max-lines-per-function: ["error", {max: 195}] -- Due to WebView message handling */
/* eslint complexity: ["error", {max: 12}] -- Due to WebView switch statement */
import { useRef, useState } from "react";

import type {
  WebViewErrorEvent,
  WebViewHttpErrorEvent,
} from "react-native-webview/lib/WebViewTypes";
import { useSelector } from "react-redux";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import {
  ScheduleAnalyticsFieldName,
  ScheduleAnalyticsSources,
  WaitingRoomAnalyticsEvent,
} from "@/constants/analytics/Scheduling";
import {
  IncomingWebViewEvent,
  OutgoingWebViewEvent,
} from "@/constants/WebView";
import { router } from "@/crossPlatform/expo-router";
import { WebView } from "@/crossPlatform/WebView";
import type {
  WebViewMessageEvent,
  WebView as WebViewRef,
} from "@/crossPlatform/WebView/types";
import useAuth from "@/hooks/useAuth";
import useWaitingRoomIntercept from "@/hooks/useWaitingRoomIntercept";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { getWebViewAccessTokenFromAuth0 } from "@/services/Auth";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { selectWaitingRoomData } from "@/store/reducers/modals/waitingRoomModal";
import JoinEventBanner from "@/ui/components/home/JoinEventBanner";
import { OpaqueSpinnerOverlay } from "@/ui/core";
import { Box, VStack } from "@/ui/layout";
import { getActorField } from "@/utils/analytics.util";
import {
  buildSurveyURL,
  getAssessmentViewerRole,
} from "@/utils/assessment.util";
import { isProductionEnvironment } from "@/utils/environment.util";
import { isWeb } from "@/utils/platform.util";
import {
  isValidWebOrigin,
  postWebViewMessage,
  webViewMessageEventToWebViewMessage,
} from "@/utils/webView.util";

const WaitingRoomModal = () => {
  const waitingRoomData = useSelector(selectWaitingRoomData);
  const { captureException, captureMessage } = useAppMonitor();
  const { track } = useAnalytics();
  const { getCredentials } = useAuth();
  const webViewRef = useRef<null | WebViewRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { externalId: appUserExternalId, isSupport: isAppUserSupport } =
    useAppUserSelector();

  const { fetchNextAssessment, joinZoom } = useWaitingRoomIntercept();

  if (!waitingRoomData) {
    if (isWeb()) router.dismiss();
    return null;
  }

  const {
    assessmentId,
    endDate,
    eventId,
    eventTitle,
    hasMore,
    isGroupClass,
    patientId,
    startDate,
    timeRange,
  } = waitingRoomData;

  const handleAssessmentCompleted = async () => {
    const actorField = getActorField({
      appUserExternalId,
      isAppUserSupport,
    });

    track(WaitingRoomAnalyticsEvent.AssessmentSubmission, {
      [AnalyticsFieldName.EventId]: eventId,
      [AnalyticsFieldName.EventTitle]: eventTitle,
      [AnalyticsFieldName.Source]: ScheduleAnalyticsSources.WaitingRoom,
      [ScheduleAnalyticsFieldName.AppointmentExternalId]: eventId,
      [ScheduleAnalyticsFieldName.ResponseExternalId]: assessmentId,
      ...actorField,
    });
    if (hasMore) {
      setIsLoading(true);
      const hasNext = await fetchNextAssessment();
      if (hasNext) return;
    }
    await joinZoom();
  };

  const getAccessToken = async (): Promise<string | undefined> => {
    if (isWeb()) {
      return await getWebViewAccessTokenFromAuth0();
    }
    const { data: credentials } = await getCredentials({
      errorScenario: "failure in waiting room webview",
      shouldForceRefresh: true,
    });
    return credentials?.accessToken;
  };

  const postAccessToken = (accessToken: string) => {
    postWebViewMessage({
      message: {
        payload: accessToken,
        type: OutgoingWebViewEvent.AccessTokenUpdated,
      },
      webViewRef,
    });
  };

  const handleWebViewMessageReceived = (message: WebViewMessageEvent) => {
    if (isWeb() && !isValidWebOrigin(message.nativeEvent.origin)) {
      // on web, only handle messages from the WebAppURL
      return;
    }
    const webViewMessage = webViewMessageEventToWebViewMessage(message);

    if (!webViewMessage) {
      captureMessage(
        `[WaitingRoom] Received invalid WebView message: ${message.nativeEvent.data}`,
      );
      return;
    }

    switch (webViewMessage.type) {
      case IncomingWebViewEvent.AssessmentProgressSaving: {
        break;
      }
      case IncomingWebViewEvent.Close: {
        break;
      }
      case IncomingWebViewEvent.Completed:
        void handleAssessmentCompleted();
        break;
      case IncomingWebViewEvent.Loaded: {
        setIsLoading(false);
        const actorField = getActorField({
          appUserExternalId,
          isAppUserSupport,
        });

        track(WaitingRoomAnalyticsEvent.AssessmentViewed, {
          [AnalyticsFieldName.EventId]: eventId,
          [AnalyticsFieldName.EventTitle]: eventTitle,
          [AnalyticsFieldName.Source]: ScheduleAnalyticsSources.WaitingRoom,
          [ScheduleAnalyticsFieldName.AppointmentExternalId]: eventId,
          [ScheduleAnalyticsFieldName.ResponseExternalId]: assessmentId,
          ...actorField,
        });
        break;
      }
      case IncomingWebViewEvent.Loading: {
        break;
      }
      case IncomingWebViewEvent.LoadingError: {
        break;
      }
      case IncomingWebViewEvent.WebViewMounted:
        void getAccessToken().then((token) => {
          if (token) {
            postAccessToken(token);
          } else {
            captureMessage(
              "[WaitingRoom] WebViewMounted but no token available",
            );
          }
        });
        break;
      case OutgoingWebViewEvent.AccessTokenUpdated: {
        break;
      }
      default:
        break;
    }
  };

  const handleWebViewError = (event: WebViewErrorEvent) => {
    setIsLoading(false);
    captureException(
      new Error(
        `[WaitingRoom] WebView load error: ${event.nativeEvent.description} (code: ${event.nativeEvent.code})`,
      ),
    );
  };

  const handleWebViewHttpError = (event: WebViewHttpErrorEvent) => {
    setIsLoading(false);
    captureMessage(
      `[WaitingRoom] WebView HTTP error: ${event.nativeEvent.statusCode} - ${event.nativeEvent.url}`,
    );
  };

  const surveyUrl = buildSurveyURL({
    queryParams: {
      appointmentExternalId: eventId,
      hasMoreThanOneSurvey: hasMore,
      isWaitingRoom: true,
      viewerRole: getAssessmentViewerRole(!isAppUserSupport),
    },
    surveyId: assessmentId,
  });

  return (
    <VStack flex={1}>
      <JoinEventBanner
        endDate={endDate}
        eventId={eventId}
        eventTitle={eventTitle}
        isGroupClass={isGroupClass}
        location="waitingRoom"
        patientId={patientId}
        startDate={startDate}
        timeRange={timeRange}
      />
      <Box flex={1}>
        {surveyUrl && (
          <WebView
            onError={handleWebViewError}
            onHttpError={handleWebViewHttpError}
            onMessage={handleWebViewMessageReceived}
            ref={webViewRef}
            source={{ uri: surveyUrl }}
            style={{ flex: 1 }}
            webviewDebuggingEnabled={!isProductionEnvironment()}
          />
        )}
        {(isLoading || !surveyUrl) && <OpaqueSpinnerOverlay />}
      </Box>
    </VStack>
  );
};

export default WaitingRoomModal;
